import InfoIcon3 from '@/../img/info-icon.svg';
import ShrinkArrowSvg from '@/../img/os-x';
import { CITY_MODES, DISCLAIMER_FOR_CITY_MODES } from '@/constants';
import { LOCALSTORAGE_MAP_INFOBOX_EXPAND } from '@/user/user-constants';
import { COUNTY } from '@/user/user-profile/user-profile-constants';
import { getPlaceStateAndCityClassification } from '@/utils/data-utils';
import { getPlatformFriendlyName } from '@/utils/string-utils';
import { strOracleApiSender } from '@/utils/stroracle-api-sender';
import { BinaryRenderer } from '@/widgets/components/binary-renderer';
import { useState } from 'react';
import { Tooltip, Whisper } from 'rsuite';
import {
  CheckboxWrapper,
  CityNameSubtitle,
  CityNameText,
  CityNameWrapper,
  DisclaimerContent,
  Dot,
  DotCaptionWrapper,
  DotDescription,
  IncludeCitiesNoticeText,
  ListingPerPlatformWrapper,
  PlatformStatsWrapper,
  ShrinkActionWrapper,
  StyledCheckbox,
  TotalText,
} from './listing-summary-styles';

const LICENSED_STR_DEFINITION =
  'Properties that are licensed as a Short-Term Rental.';
const UNLICENSED_STR_DEFINITION =
  'Properties that are not licensed as a Short-Term Rental but have either taken STR bookings or have live listings with STR availability.';
const UNIDENTIFIED_STR_DEFINITION =
  'Listings that have either taken Short-Term Rental bookings or are live listings with STR availability that we have not as yet been able to identify.';
const NON_STR_PROPERTIES_DEFINITION =
  'Properties & listings that have not been confirmed to be acting as a Short-Term Rental in the past year or coming three months';
const IDENTIFIED_STR_DEFINITION =
  'Properties that are licensed as a Short-Term Rental or have either taken STR bookings or have live listings with STR availability.';

function MessageTooltip({ tooltip }) {
  return (
    <Whisper
      trigger="hover"
      placement="top"
      speaker={<Tooltip>{tooltip}</Tooltip>}
    >
      <button
        type="button"
        className="ml-[10px] w-[16px] h-[16px]"
        aria-label={tooltip}
      >
        <InfoIcon3 className="fill-white" aria-hidden />
      </button>
    </Whisper>
  );
}

export function ShrinkActionButton(props) {
  return (
    <ShrinkActionWrapper onClick={props.onClick}>
      <button
        type="button"
        aria-label="Close"
        className="block w-[16px] h-[16px] fill-white"
      >
        <ShrinkArrowSvg aria-hidden />
      </button>
    </ShrinkActionWrapper>
  );
}

export function ListingCityName({ city, cityMode, subtitle }) {
  return (
    <CityNameWrapper>
      <CityNameText>{city}</CityNameText>
      <BinaryRenderer checkpoint={cityMode !== CITY_MODES.customer}>
        <CityNameSubtitle subtitle={subtitle} cityMode={cityMode} />
      </BinaryRenderer>
    </CityNameWrapper>
  );
}

export function TotalListings(props) {
  return <TotalText>{`Total: ${props.totalListings}`}</TotalText>;
}

function ContactOurTeam() {
  return (
    <a
      href="mailto:rentalscape@deckard.com?subject=Sales Inquiry"
      style={{
        color: 'rgba(255, 255, 255, 0.65)',
        textDecoration: 'underline',
      }}
    >
      contact our team
    </a>
  );
}

export function Disclaimer({ cityMode }) {
  return (
    <BinaryRenderer checkpoint={cityMode !== CITY_MODES.customer}>
      <DisclaimerContent>
        {DISCLAIMER_FOR_CITY_MODES[cityMode]}
        <ContactOurTeam />.
      </DisclaimerContent>
    </BinaryRenderer>
  );
}

export function PlatformStatsOfListings({
  uniquePlatforms,
  platformListings,
  platformToDisplayConfig,
}) {
  return (
    <PlatformStatsWrapper>
      {uniquePlatforms.map((p) => (
        <ListingPerPlatform
          key={Math.random()}
          platformLabel={getPlatformFriendlyName(p, platformToDisplayConfig)}
          count={platformListings[p]}
        />
      ))}
    </PlatformStatsWrapper>
  );
}

function ListingPerPlatform({ platformLabel, count }) {
  return (
    <ListingPerPlatformWrapper>
      {`${platformLabel}: ${count}`}
    </ListingPerPlatformWrapper>
  );
}

function IdentifiedPropertyDots({
  licensedIdentifiedCount,
  unlicensedIdentifiedCount,
  displayUnlicensedPropertiesSeparately,
  labels,
}) {
  if (displayUnlicensedPropertiesSeparately) {
    return (
      <>
        <DotDescription>
          <Dot color="#37A3BC" />
          <span>
            {`${
              labels?.licensed || 'Licensed STR Properties'
            } (${licensedIdentifiedCount})`}
          </span>
          <MessageTooltip tooltip={LICENSED_STR_DEFINITION} />
        </DotDescription>

        <DotDescription>
          <Dot color="#E92329" />
          <span>
            {`${
              labels?.unlicensed || 'Unlicensed Identified STR Properties'
            } (${unlicensedIdentifiedCount})`}
          </span>
          <MessageTooltip tooltip={UNLICENSED_STR_DEFINITION} />
        </DotDescription>
      </>
    );
  }

  const totalCount = licensedIdentifiedCount + unlicensedIdentifiedCount;

  return (
    <DotDescription>
      <Dot color="#37A3BC" />
      <span>Identified STR Properties ({totalCount})</span>
      <MessageTooltip tooltip={IDENTIFIED_STR_DEFINITION} />
    </DotDescription>
  );
}

export function DotCaption({
  licensedIdentifiedCount,
  unlicensedIdentifiedCount,
  activeCount,
  inactiveCount,
  displayUnlicensedPropertiesSeparately,
  labels,
}) {
  return (
    <DotCaptionWrapper>
      <IdentifiedPropertyDots
        licensedIdentifiedCount={licensedIdentifiedCount}
        unlicensedIdentifiedCount={unlicensedIdentifiedCount}
        displayUnlicensedPropertiesSeparately={
          displayUnlicensedPropertiesSeparately
        }
        labels={labels}
      />
      <DotDescription>
        <Dot color="#FFC64A" />
        <span>
          {`${
            labels?.unidentified || 'Unidentified Live STR Listings'
          } (${activeCount})`}
        </span>
        <MessageTooltip tooltip={UNIDENTIFIED_STR_DEFINITION} />
      </DotDescription>
      <DotDescription>
        <Dot color="#A5B1B9" />
        <span>{`${labels?.other || 'Other Listings'} (${inactiveCount})`}</span>
        <MessageTooltip tooltip={NON_STR_PROPERTIES_DEFINITION} />
      </DotDescription>
    </DotCaptionWrapper>
  );
}

const handleCheckboxOnClick = (value, checked, setShow) => {
  localStorage.setItem(value, !checked);
  setShow(checked);
};

export function DonotShowAgain() {
  const [show, setShow] = useState(
    !JSON.parse(localStorage.getItem(LOCALSTORAGE_MAP_INFOBOX_EXPAND))
  );
  return (
    <CheckboxWrapper>
      <StyledCheckbox
        value={LOCALSTORAGE_MAP_INFOBOX_EXPAND}
        checked={show}
        onChange={(value, checked) =>
          handleCheckboxOnClick(value, checked, setShow)
        }
      >
        Don&apos;t show again
      </StyledCheckbox>
    </CheckboxWrapper>
  );
}

/**
 * Detects if a given place is a county based on some fields from
 * its stats and its ID
 * @param {string} place ID of the place (e.g. 'ca-placer')
 * @param {{countyns: string, placens: string}} statsNSFields `countyns` and `placens` fields
 * from the `rentalscape` object in the place's stats.
 * @returns {boolean} True iff the place is likely a county
 */
function isPlaceCounty(place, statsNSFields) {
  const { placens, countyns } = statsNSFields;
  if (countyns) return true;
  if (placens) return false;

  // If a place has no countyns or placens in its stats, make a guess
  // based on the place's ID
  return getPlaceStateAndCityClassification(place).placeType === COUNTY;
}

const INCLUDE_CITIES_MESSAGE =
  'All properties including within Incorporated Cities shown';
const EXCLUDE_CITIES_MESSAGE = 'Only unincorporated properties shown';

/**
 * Displays a small label in the map's info box with a message
 * about whether cities are included among the displayed listings/properties.
 * Renders itself only when current place is a county
 *
 * @param {string} props.currentPlace Current place ID (e.g. `ca-placer`)
 * @param {{countyns: string, placens: string}} props.currentPlaceNSFields `countyns` and `placens`
 * fields from the stats for the current place
 * @returns {JSX.Element|null}
 * @constructor
 */
export function IncludeCitiesNotice(props) {
  const { currentPlaceNSFields, currentPlace, customerConfigIncludesCities } =
    props;
  const isIncludeCities =
    strOracleApiSender.getIncludeCities() || customerConfigIncludesCities;
  const isCurrentPlaceCounty = isPlaceCounty(
    currentPlace,
    currentPlaceNSFields
  );

  if (!isCurrentPlaceCounty) return null;

  return (
    <IncludeCitiesNoticeText>
      {isIncludeCities ? INCLUDE_CITIES_MESSAGE : EXCLUDE_CITIES_MESSAGE}
    </IncludeCitiesNoticeText>
  );
}
